/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import CircularProgress from '@mui/material/CircularProgress';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';
import { useModalBackHandler } from 'src/hooks/use-modal-back-handler';

import Image from '../image';
import Iconify from '../iconify';
import Player from '../player/player';
import { useSettingsContext } from '../settings';
import { CustomRadioGroup } from '../radio-button';
import {
  BaseDialogProps,
  VideoDialogProps,
  RadioButtonDialogProps,
  ThumbnailWithTimerDialogProps,
} from './types';

export function BaseDialog({
  title,
  content,
  action,
  onClose,
  open,
  cancel,
  onCancel,
  accept,
  onAccept,
  actionButtonsDirection = 'ltr',
  children,
  dialogTitleProps,
  dialogContentProps,
  dialogActionProps,
  isFullScreenOnMobile = false,
  withCloseIcon = false,
  isButtonFullWidth = false,
  buttonColor = 'inherit',
  cancelButtonProps,
  confirmButtonProps,
  acceptButtonProps,
  hideCancelButton,
  disableBackdropClick = false,
  sx,
  ...other
}: Readonly<BaseDialogProps>) {
  const settings = useSettingsContext();

  const { t } = useTranslation();

  const isMobile = useResponsive('down', 'md');

  // 배경/ESC는 handleDialogClose에서 무시. 뒤로가기 제스처는 취소와 동일하게 onCancel 후 닫기(성적 확인 모달 등)
  const handleHardwareBackClose = useCallback(() => {
    if (disableBackdropClick) {
      onCancel?.();
    }
    onClose();
  }, [disableBackdropClick, onCancel, onClose]);

  useModalBackHandler(open, handleHardwareBackClose);

  const handleDialogClose = useCallback(
    (_event: object, reason: string) => {
      // disableBackdropClick: 시험 제출 완료 모달 등 — 딤/ESC로만 닫히면 응시 화면 잔류·재제출 이슈 방지
      if (disableBackdropClick && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
        return;
      }
      onClose();
    },
    [disableBackdropClick, onClose]
  );

  const handleAccept = () => {
    // we can conditionally call onAccetp function
    if (onAccept) {
      onAccept();
    }
  };

  return (
    <Dialog
      dir={settings.themeDirection}
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleDialogClose}
      fullScreen={isFullScreenOnMobile && isMobile}
      sx={{
        zIndex: 99999,
        ...sx,
      }}
      {...other}
    >
      {!withCloseIcon ? (
        <DialogTitle sx={{ pb: 2, pt: { xs: 2 } }} variant="h5" {...dialogTitleProps}>
          {title}
        </DialogTitle>
      ) : (
        <DialogTitle {...dialogTitleProps}>
          <Stack justifyContent="center" alignItems="center" gap={8} direction="row">
            {title && (
              <Typography paddingX={4} variant="h4">
                {title}
              </Typography>
            )}

            <IconButton onClick={onCancel ?? onClose} sx={{ position: 'absolute', left: 16 }}>
              <Iconify icon="mdi:close" />
            </IconButton>
          </Stack>
        </DialogTitle>
      )}

      <DialogContent {...dialogContentProps}>
        {content && (
          <Box>
            {typeof content === 'string' ? <Typography>{content}</Typography> : <>{content}</>}
          </Box>
        )}

        {children}
      </DialogContent>

      <DialogActions
        sx={{
          pb: 2,
        }}
        {...dialogActionProps}
      >
        {actionButtonsDirection === 'ltr' && !withCloseIcon && !hideCancelButton && (
          <Button
            variant={accept ? 'outlined' : 'text'}
            color={buttonColor}
            onClick={onCancel ?? onClose}
            sx={{ color: buttonColor }}
            {...cancelButtonProps}
          >
            {cancel ?? t('learner.36')}
          </Button>
        )}

        {action}
        {accept && (
          <Button
            variant="contained"
            fullWidth={isButtonFullWidth}
            color={buttonColor}
            onClick={handleAccept}
            {...acceptButtonProps}
          >
            {accept}
          </Button>
        )}

        {actionButtonsDirection === 'rtl' && !withCloseIcon && !hideCancelButton && (
          <Button
            variant={accept ? 'outlined' : 'text'}
            color={buttonColor}
            onClick={onCancel ?? onClose}
            sx={{ color: buttonColor }}
            {...cancelButtonProps}
          >
            {cancel ?? t('learner.36')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function RadioButtonDialog({
  radioButtonLabel,
  radioButtonList,
  ...other
}: RadioButtonDialogProps) {
  const [selectedOption, setSelectedOption] = useState<any>();
  const { onAccept, accept, ...remainingProps } = { ...other };

  const { t } = useTranslation();

  return (
    <BaseDialog
      onAccept={() => onAccept(selectedOption)}
      accept={accept ?? t('learner.74')}
      {...remainingProps}
    >
      <CustomRadioGroup
        options={radioButtonList}
        label={radioButtonLabel}
        onChange={setSelectedOption}
      />
    </BaseDialog>
  );
}

export function VideoDialog({ videoPath, videoStyle, ...other }: VideoDialogProps) {
  const videoPlay = useBoolean();

  const handleReady = useCallback(() => {
    setTimeout(() => videoPlay.onTrue(), 500);
  }, [videoPlay]);

  return (
    <BaseDialog {...other}>
      <Player
        style={{ aspectRatio: '16/9', ...videoStyle }}
        controls
        url={videoPath}
        playing={videoPlay.value}
        onReady={handleReady}
        onEnded={videoPlay.onFalse}
        onPlay={videoPlay.onTrue}
        onPause={videoPlay.onFalse}
      />
    </BaseDialog>
  );
}

export function ThumbnailWithTimerDialog({
  timer,
  imageUrl,
  onTimerEnd,
  ...other
}: ThumbnailWithTimerDialogProps) {
  const { open } = { ...other };
  const [count, setCount] = useState<number>(timer);

  const commonStyle: React.CSSProperties = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (open) {
      if (count > 0) {
        intervalId = setInterval(() => {
          setCount((prevCount) => {
            if (prevCount <= 1) {
              clearInterval(intervalId);
              return 0;
            }
            return prevCount - 1;
          });
        }, 1000);
      }
    } else {
      return setCount(timer);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [open]);

  useEffect(() => {
    if (count === 0) {
      onTimerEnd();
    }
  }, [count]);

  const progressValue = (count / timer) * 100;

  return (
    <BaseDialog {...other}>
      <Box position="relative">
        {imageUrl ? (
          <Image
            overlay={count !== 0 ? 'rgba(0, 0, 0, 0.7)' : 'none'}
            ratio="16/9"
            src={imageUrl}
          />
        ) : (
          <Box height={64} />
        )}
        <Box zIndex={9} sx={commonStyle}>
          {count > 0 && (
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress variant="determinate" value={progressValue} size={48} />
              <Box sx={commonStyle}>
                <Typography variant="caption" component="div" sx={{ color: 'primary.main' }}>
                  {count}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </BaseDialog>
  );
}
